import type { ClientGroup } from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientGroupMembershipStatusEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { LegendListRenderItemProps } from '@legendapp/list/react-native';
import { AnimatedLegendList } from '@legendapp/list/reanimated';
import { useScrollToTop } from '@react-navigation/native';
import { useCallback, useMemo, useRef, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Divider, Text } from 'react-native-paper';

import { useGetGroups } from '@/features/api/hooks/grapevine-group/useGetGroups';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { GroupCreateButton } from '@/features/groups/components/GroupCreateButton';
import { GroupListItem } from '@/features/groups/components/GroupListItem';
import { MeinOrtGroupCreateButton } from '@/features/mein-ort/components/MeinOrtGroupCreateButton';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type GroupsSection = {
  key: string;
  title: string;
  groups: ClientGroup[];
  isMyGroup: boolean;
  headerAction?: ReactNode;
  emptyDescription: string;
  emptyFooter?: ReactNode;
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    list: { flex: 1 },
    content: {
      flexGrow: 1
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 40,
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 4
    },
    headerText: {
      color: theme.colors.onSurface,
      fontSize: 16,
      fontWeight: '500',
      lineHeight: 24
    },
    card: {
      marginHorizontal: 12,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    empty: { marginHorizontal: 16, marginTop: 8 }
  });

export const MeinOrtGroupsTab: FC = () => {
  const theme = useAppTheme();
  const { scrollY, feedInset } = useMeinOrtScroll();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.groups'
  });
  const { t: tState } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace.state'
  });

  const { getFeature } = useFeature();
  const isGroupCreationEnabled = getFeature(
    FeaturesEnum.GroupCreation
  )?.enabled;

  const {
    data: groups,
    refetch,
    isPending,
    isRefetching,
    isError
  } = useGetGroups();

  const myGroups = useMemo(
    () =>
      groups?.filter(
        ({ membershipStatus }) =>
          membershipStatus === ClientGroupMembershipStatusEnum.Approved
      ) ?? [],
    [groups]
  );

  const areaGroups = useMemo(
    () =>
      groups?.filter(
        ({ membershipStatus }) =>
          membershipStatus !== ClientGroupMembershipStatusEnum.Approved
      ) ?? [],
    [groups]
  );

  const sections = useMemo<GroupsSection[]>(
    () => [
      {
        key: 'my',
        title: t('my.title'),
        groups: myGroups,
        isMyGroup: true,
        headerAction: isGroupCreationEnabled ? (
          <MeinOrtGroupCreateButton />
        ) : undefined,
        emptyDescription: t('my.empty'),
        emptyFooter: isGroupCreationEnabled ? <GroupCreateButton /> : undefined
      },
      {
        key: 'area',
        title: t('area.title'),
        groups: areaGroups,
        isMyGroup: false,
        emptyDescription: t('area.empty'),
        emptyFooter: isGroupCreationEnabled ? <GroupCreateButton /> : undefined
      }
    ],
    [myGroups, areaGroups, t, isGroupCreationEnabled]
  );

  const listRef = useRef(null);
  useScrollToTop(listRef);

  const sharedValues = useMemo(() => ({ scrollOffset: scrollY }), [scrollY]);

  const renderSection = useCallback(
    ({
      item
    }: LegendListRenderItemProps<GroupsSection, string | undefined>) => (
      <View>
        <View style={styles.header}>
          <Text style={styles.headerText}>{item.title}</Text>
          {item.headerAction}
        </View>
        {hasItems(item.groups) ? (
          <View style={styles.card}>
            {item.groups.map((group, index) => (
              <View key={group.id}>
                {index > 0 ? <Divider /> : undefined}
                <GroupListItem group={group} isMyGroup={item.isMyGroup} />
              </View>
            ))}
          </View>
        ) : (
          <EmptyCard
            style={styles.empty}
            description={item.emptyDescription}
            footer={item.emptyFooter}
          />
        )}
      </View>
    ),
    [styles]
  );

  if (isPending) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <ActivityIndicator size={48} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <Text variant="titleMedium">{tState('error.title')}</Text>
        <Text variant="bodyMedium">{tState('error.subtitle')}</Text>
        <Button mode="contained-tonal" onPress={() => refetch()}>
          {tState('error.retry')}
        </Button>
      </View>
    );
  }

  return (
    <AnimatedLegendList<GroupsSection>
      ref={listRef}
      style={styles.list}
      data={sections}
      refreshControl={
        <Refresher refreshing={isRefetching} onRefresh={refetch} />
      }
      renderItem={renderSection}
      recycleItems
      sharedValues={sharedValues}
      keyExtractor={(item) => item.key}
      contentContainerStyle={[styles.content, { paddingTop: feedInset }]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    />
  );
};
